import React, { useCallback, useEffect, useState } from "react";

import type { ValidateErrorEntity } from "rc-field-form/lib/interface";

import { getReferrer, signupUser } from "services";

import { getLocalKlimVar, mapAppTypeToString, useAppType, useGeneralContext, useGetFarmerType, useToggleNavs, useTranslation } from "utils";

import { Alert, Col, ErrorBoundary, Form, IllustrationCard, Input, Link, MainPage, Row, ScreenSplit, Svg, Message as message } from "components";

import { IntroScreen, IntroScreenNativeMobile } from "pages/app/account/components/IntroScreen.tsx";
import { SignupButtons } from "pages/app/account/components/SignupButtons.tsx";
import { SignupCheckbox } from "pages/app/account/components/SignupCheckbox.tsx";

interface Referrer {
  name: string;
  // Add other referrer properties as needed
}

interface SignupFormValues {
  email: string;
  terms: boolean;
  // Add other form fields as needed
}

interface ShareData {
  title: string;
  summary: string;
  link: string;
  image: string;
}

export const SignUp: React.FC = () => {
  const { t } = useTranslation(["signinup", "meta"]);

  const [loading, setLoading] = useState<boolean>(false);
  const [referrer, setReferrer] = useState<Referrer | null>(null);
  const [referralToken, setReferralToken] = useState<string>("");
  const [completed, setCompleted] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const appType = useAppType();
  const [showIntroPage, setShowIntroPage] = useState<boolean>(appType.app);
  const { setReferralBanner } = useGeneralContext();

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const partnerParam = searchParams.get("partner");

    if (partnerParam) {
      // Store it in localStorage so checkReferrer can find it
      const klimSession = JSON.parse(localStorage.getItem("klimSession") || "{}");
      klimSession.partner = partnerParam;
      localStorage.setItem("klimSession", JSON.stringify(klimSession));
    }
  }, []);

  useToggleNavs(false, appType.mobile);

  const farmerType = useGetFarmerType();

  const [share] = useState<ShareData>({
    title: t("meta:signUp.title"),
    summary: t("meta:signUp.description"),
    link: `${process.env.REACT_APP_FARM_URL}/sign-up`,
    image: `${process.env.REACT_APP_FARM_URL}/images/misc/share.png`,
  });

  const loadUserReferrer = useCallback(() => {
    const userToken = getLocalKlimVar("referrer");

    // Only fetch if token exists and is not null/empty
    if (!userToken || userToken === "null") {
      return;
    }

    const controller = new AbortController();

    getReferrer({
      referralToken: userToken,
      signal: controller.signal,
    })
      .then((res) => {
        setReferrer(res.data || res);
        setReferralToken(userToken);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("Failed to load user referrer:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const loadPartnerReferrer = useCallback(() => {
    const partnerToken = getLocalKlimVar("partner");

    // Only fetch if token exists and is not null/empty
    if (!partnerToken || partnerToken === "null") {
      loadUserReferrer();
      return;
    }

    const controller = new AbortController();

    getReferrer({
      referralToken: partnerToken,
      signal: controller.signal,
    })
      .then((res) => {
        setReferrer(res.data || res);
        setReferralToken(partnerToken);
      })
      .catch((_err) => {
        loadUserReferrer();
      })
      .finally(() => {
        setLoading(false);
      });
  }, [loadUserReferrer]);

  const checkReferrer = useCallback(() => {
    const partnerToken = getLocalKlimVar("partner");
    const userToken = getLocalKlimVar("referrer");

    // Only check if we have valid tokens (not null or empty)
    if (partnerToken && partnerToken !== "null") {
      loadPartnerReferrer();
    } else if (userToken && userToken !== "null") {
      loadUserReferrer();
    }
  }, [loadPartnerReferrer, loadUserReferrer]);

  const complete = (v: SignupFormValues): void => {
    setLoading(true);
    setEmail(v.email);
    const controller = new AbortController();

    signupUser({
      ...v,
      ...{
        referrer: referralToken,
        appType: mapAppTypeToString(appType),
        utm_source: getLocalKlimVar("utm_source"), // eslint-disable-line camelcase
        utm_campaign: getLocalKlimVar("utm_campaign"),
        utm_medium: getLocalKlimVar("utm_medium"),
        farmerType: farmerType,
        signal: controller.signal,
      },
    })
      .then(() => {
        setCompleted(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const incomplete = (v: ValidateErrorEntity<SignupFormValues>): void => {
    if (!v.values.terms || !v.values.email) {
      message({ content: t("errors.mandatoryFields"), icon: "error" });
      return;
    }
    // REGEX to check email validity
    if (v.values.email && !v.values.email.match(/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/)) {
      message({ content: t("errors.invalidEmail"), icon: "error" });
      return;
    }
    if (!v.values.terms) {
      message({ content: t("errors.checkboxRequired"), icon: "error" });
    }
  };

  const handleLink = (): void => {
    const controller = new AbortController();

    signupUser({
      email: email,
      signal: controller.signal,
    }).finally(() => {
      setLoading(false);
    });
  };

  useEffect(() => {
    if (referrer?.name) {
      const bannerText = t("signup.referral.description", {
        referrersName: referrer.name,
      });

      setReferralBanner({
        text: bannerText,
      });
    }

    return () => {
      setReferralBanner(null);
    };
  }, [referrer, setReferralBanner, t]);

  useEffect(() => {
    checkReferrer();
  }, [checkReferrer]);

  const NativeAppIntro: React.FC = () => (
    <div className="flex-center margin-left-100 margin-right-100">
      <IntroScreenNativeMobile className="margin-bottom-400" farmerType={farmerType} />
      <SignupButtons
        loading={loading}
        onClick={() => {
          setShowIntroPage(false);
        }}
      />
    </div>
  );

  const NormalIntro: React.FC = () => (
    <section className="container">
      <div className="mini-form">
        <Row>
          <Col className="left">
            <h2>{t("signup.title")}</h2>
            <p className="margin-top-100 margin-bottom-0">{t("signup.description")}</p>
          </Col>
        </Row>
        <Form name="signup" className="margin-top-400" onFinish={(v) => complete(v)} onFinishFailed={(v) => incomplete(v)}>
          <Row>
            <Col>
              <Input
                dataTestId="sign-up-input-email"
                name="email"
                label={t("signup.inputs.email")}
                rules={[
                  {
                    required: true,
                    type: "email",
                  },
                ]}
              />
            </Col>
          </Row>
          <ErrorBoundary>
            <SignupCheckbox />
          </ErrorBoundary>
          <ErrorBoundary>
            <SignupButtons loading={loading} />
          </ErrorBoundary>
        </Form>
      </div>
    </section>
  );

  const screenToDisplay = showIntroPage ? <NativeAppIntro /> : <NormalIntro />;

  return (
    <ScreenSplit sideChildren={<IntroScreen farmerType={farmerType} />}>
      <MainPage
        loading={loading}
        pageName={"sign-in-up sign-up"}
        share={share}
        containerClass={appType.mobile ? "small" : ""}
        className={appType.app ? "sign-in-up-container-native-mobile" : "sign-in-up-container"}
      >
        {referrer?.name && !completed && (
          <Alert
            dataTestId="alert-banner-signup"
            className="alert-banner margin-bottom-200"
            description={t("signup.referral.description", {
              referrersName: referrer.name,
            })}
            icon=""
            type="green"
            closable={true}
          />
        )}

        {completed && (
          <section className="container">
            <div className="mini-form">
              <Row>
                <Col className="left margin-bottom-350">
                  <h2>{t("signedUp.title")}</h2>
                </Col>
              </Row>
              <Row>
                <Col>
                  <IllustrationCard>
                    <Svg svgName="misc/klim_message" />
                  </IllustrationCard>
                </Col>
              </Row>
              <Row>
                <Col>
                  <p>{t("signedUp.description")}</p>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Link onClick={handleLink}>{t("signedUp.link")}</Link>
                </Col>
              </Row>
            </div>
          </section>
        )}
        {!completed && screenToDisplay}
      </MainPage>
    </ScreenSplit>
  );
};
